import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ContentItem } from "@/types/content";

import { PdfViewer } from "./pdf/PdfViewer";
import { ConversionPendingPlaceholder } from "./viewers/ConversionPendingPlaceholder";
import { MediaContentViewer } from "./media/MediaContentViewer";

interface ContentViewerProps {
  content: ContentItem;
}

// Extended content type with conversion fields
interface ContentWithConversion extends ContentItem {
  pdfStoragePath?: string | null;
  conversionStatus?: "pending" | "processing" | "ready" | "failed" | "not_needed" | null;
}

/**
 * Hook to get signed URL for private bucket files
 */
function useSignedUrl(storagePath?: string | null) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storagePath) {
      setSignedUrl(null);
      setLoading(false);
      return;
    }

    async function getUrl() {
      setLoading(true);
      const { data, error } = await supabase.storage
        .from("content-uploads")
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        console.error("Failed to get signed URL:", error);
        setSignedUrl(null);
      } else {
        setSignedUrl(data.signedUrl);
      }
      setLoading(false);
    }

    getUrl();
  }, [storagePath]);

  return { signedUrl, loading };
}

/**
 * Hook to fetch conversion status and subscribe to realtime updates
 */
function useConversionStatus(contentId: string) {
  const [conversionData, setConversionData] = useState<{
    pdfStoragePath: string | null;
    conversionStatus: string | null;
  }>({ pdfStoragePath: null, conversionStatus: null });

  useEffect(() => {
    // Initial fetch
    async function fetchStatus() {
      const { data } = await supabase
        .from("content")
        .select("pdf_storage_path, conversion_status")
        .eq("id", contentId)
        .maybeSingle();

      if (data) {
        setConversionData({
          pdfStoragePath: data.pdf_storage_path,
          conversionStatus: data.conversion_status,
        });
      }
    }

    fetchStatus();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`content-${contentId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "content",
          filter: `id=eq.${contentId}`,
        },
        (payload) => {
          const updated = payload.new as {
            pdf_storage_path?: string | null;
            conversion_status?: string | null;
          };
          setConversionData({
            pdfStoragePath: updated.pdf_storage_path ?? null,
            conversionStatus: updated.conversion_status ?? null,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contentId]);

  return conversionData;
}

/**
 * Master router for all content types.
 * Routes everything to PDFViewer when possible (unified PDF architecture).
 */
export function ContentViewer({ content }: ContentViewerProps) {
  const { type, sourceUrl, storagePath } = content;
  const { pdfStoragePath, conversionStatus } = useConversionStatus(content.id);

  // Get signed URL for private bucket files (for non-PDF media)
  const { signedUrl, loading } = useSignedUrl(storagePath);

  // --- CHAT (not viewable in document viewer) ---
  if (type === "chat") {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Chat content is viewed in the Chat panel
      </div>
    );
  }

  // --- MEDIA CONTENT (youtube, video, audio, recording) ---
  if (["youtube", "video", "audio", "recording"].includes(type)) {
    return (
      <MediaContentViewer
        content={content}
        signedUrl={signedUrl}
        loading={loading}
      />
    );
  }

  // --- PDF-CONVERTIBLE TYPES (pdf, doc, web, text) ---
  // Unified PDF rendering path

  // Determine the PDF path to use
  const effectivePdfPath = pdfStoragePath || (type === "pdf" ? storagePath : null);

  // If PDF is ready, render it
  if (effectivePdfPath && (conversionStatus === "ready" || conversionStatus === "not_needed" || type === "pdf")) {
    return <PdfViewer storagePath={effectivePdfPath} contentId={content.id} />;
  }

  // Handle conversion states
  if (conversionStatus === "failed") {
    const canRetry = type !== "text" && (Boolean(storagePath) || Boolean(sourceUrl));

    return (
      <ConversionPendingPlaceholder
        status="failed"
        onRetry={
          canRetry
            ? () => {
                // Trigger re-conversion
                supabase.functions.invoke("convert-content-to-pdf", {
                  body: {
                    contentId: content.id,
                    type,
                    storagePath,
                    sourceUrl,
                  },
                });
              }
            : undefined
        }
      />
    );
  }

  if (conversionStatus === "processing") {
    return <ConversionPendingPlaceholder status="processing" />;
  }

  if (conversionStatus === "pending") {
    return <ConversionPendingPlaceholder status="pending" />;
  }

  // Fallback for types without storage path
  if (!storagePath && !sourceUrl) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Content file missing or not accessible
      </div>
    );
  }

  // Default: show pending state while we figure out conversion
  return <ConversionPendingPlaceholder status="pending" />;
}
